// @ts-strict-ignore
import invariant from 'invariant';
import cloneDeep from 'lodash.clonedeep';
import { Dispatch } from 'redux';

import {
  Org as ApiOrg,
  addAdmin as apiAddAdmin,
  addOwner as apiAddOwner,
  approveAdminRequest as apiApproveAdminRequest,
  deleteOrg as apiDeleteOrg,
  fetchOrg as apiFetchOrg,
  rejectAdminRequest as apiRejectAdminRequest,
  removeAdmin as apiRemoveAdmin,
} from 'api/orgAdmin';
import { OrgSize } from 'api/types/OrgSize';
import { PreferredListingType } from 'api/types/PreferredListingType';
import { MainStoreErrorAction, setError } from 'modules/main/store/ducks/error';
import {
  MainStoreUserEnvironmentAction,
  MainStoreUserEnvironmentState,
  updateUserEnvironment,
} from 'modules/main/store/ducks/userEnvironment';

// Types

export type MainStoreOrgDashboardOrgsOrg = ApiOrg & {
  isFetching: boolean;
};

export type OrgUserPreferences = {
  preferredListingTypes: PreferredListingType[] | undefined;
  orgSize: OrgSize | undefined;
};

export type MainStoreOrgDashboardOrgsState = {
  byId: Record<string, MainStoreOrgDashboardOrgsOrg>;
  orgUserPreferences: OrgUserPreferences | undefined;
};

type FetchOrgRequestAction = {
  type: 'ORG_DASHBOARD_FETCH_ORG_REQUEST';
  orgId: string;
};

type FetchOrgSuccessAction = {
  type: 'ORG_DASHBOARD_FETCH_ORG_SUCCESS';
  org: ApiOrg;
  user: { preferredListingTypes: PreferredListingType[]; orgSize: OrgSize };
};

type DeleteOrgAction = {
  type: 'ORG_DASHBOARD_DELETE_ORG';
  orgId: string;
};

type UpdateOrgAction = {
  type: 'ORG_DASHBOARD_UPDATE_ORG';
  org: ApiOrg;
};

type PartialUpdateOrgAction = {
  type: 'ORG_DASHBOARD_PARTIAL_UPDATE_ORG';
  values: Partial<ApiOrg>;
};

type UpdateOrgUserPreferences = {
  type: 'ORG_DASHBOARD_UPDATE_ORG_USER_PREFERENCES';
  orgUserPreferences: OrgUserPreferences;
};

export type MainStoreOrgDashboardOrgsAction =
  | FetchOrgRequestAction
  | FetchOrgSuccessAction
  | DeleteOrgAction
  | UpdateOrgAction
  | PartialUpdateOrgAction
  | UpdateOrgUserPreferences;

// Reducer

export function orgsReducer(
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/default-param-last
  state: MainStoreOrgDashboardOrgsState = {
    byId: {},
    orgUserPreferences: undefined,
  },
  action: MainStoreOrgDashboardOrgsAction
) {
  switch (action.type) {
    case 'ORG_DASHBOARD_FETCH_ORG_REQUEST':
      return {
        ...state,
        byId: {
          ...state.byId,
          [action.orgId]: {
            ...state.byId[action.orgId],
            id: action.orgId,
            isFetching: true,
          },
        },
      };

    case 'ORG_DASHBOARD_FETCH_ORG_SUCCESS':
      return {
        ...state,
        byId: {
          ...state.byId,
          [action.org.id]: {
            ...action.org,
            isFetching: false,
          },
        },
        orgUserPreferences: action.user,
      };

    case 'ORG_DASHBOARD_DELETE_ORG': {
      const newState = { ...state };
      delete newState.byId[action.orgId];
      return newState;
    }

    case 'ORG_DASHBOARD_UPDATE_ORG': {
      return {
        ...state,
        byId: {
          ...state.byId,
          [action.org.id]: {
            ...state.byId[action.org.id],
            ...action.org,
          },
        },
      };
    }

    case 'ORG_DASHBOARD_PARTIAL_UPDATE_ORG': {
      return {
        ...state,
        byId: {
          ...state.byId,
          [action.values.id]: {
            ...state.byId[action.values.id],
            ...action.values,
          },
        },
      };
    }

    case 'ORG_DASHBOARD_UPDATE_ORG_USER_PREFERENCES': {
      return {
        ...state,
        orgUserPreferences: action.orgUserPreferences,
      };
    }

    default:
      return state;
  }
}

// Actions

export function fetchOrgRequest(orgId: string): FetchOrgRequestAction {
  return {
    type: 'ORG_DASHBOARD_FETCH_ORG_REQUEST',
    orgId,
  };
}

export function fetchOrgSuccess({
  org,
  user,
}: {
  org: ApiOrg;
  user: {
    preferredListingTypes: PreferredListingType[];
    orgSize: OrgSize;
  };
}): FetchOrgSuccessAction {
  return {
    type: 'ORG_DASHBOARD_FETCH_ORG_SUCCESS',
    org,
    user,
  };
}

export function fetchOrg(orgId: string, force = false) {
  return (
    dispatch: Dispatch<MainStoreOrgDashboardOrgsAction | MainStoreErrorAction>,
    getState: () => { orgDashboard: { orgs: MainStoreOrgDashboardOrgsState } }
  ) => {
    const state = getState();
    const org = state.orgDashboard.orgs.byId[orgId];

    if (!org || force) {
      dispatch(fetchOrgRequest(orgId));
      apiFetchOrg(orgId)
        .then((data) => dispatch(fetchOrgSuccess(data)))
        .catch((error) => {
          dispatch(setError(error));
        });
    }
  };
}

export function updateOrg(org: ApiOrg): UpdateOrgAction {
  return {
    type: 'ORG_DASHBOARD_UPDATE_ORG',
    org,
  };
}

export function deleteOrg(orgId: string) {
  return (
    dispatch: Dispatch<
      | MainStoreUserEnvironmentAction
      | MainStoreOrgDashboardOrgsAction
      | MainStoreErrorAction
    >,
    getState: () => { userEnvironment: MainStoreUserEnvironmentState }
  ) => {
    const userEnvironment = cloneDeep(getState().userEnvironment);
    userEnvironment.user.orgs = userEnvironment.user.orgs.filter(
      (o) => o.id !== orgId
    );
    return apiDeleteOrg(orgId)
      .then(() => {
        dispatch({
          type: 'ORG_DASHBOARD_DELETE_ORG',
          orgId,
        });
        dispatch(updateUserEnvironment(userEnvironment));
      })
      .catch((error) => {
        dispatch(setError(error));
      });
  };
}

export function addAdmin(orgId: string, email: string, isOwner: boolean) {
  return (
    dispatch: Dispatch<MainStoreOrgDashboardOrgsAction>,
    getState: () => { orgDashboard: { orgs: MainStoreOrgDashboardOrgsState } }
  ) => {
    const add = isOwner ? apiAddOwner : apiAddAdmin;
    return add(orgId, email).then(({ user, invite }) => {
      const org = getState().orgDashboard.orgs.byId[orgId];
      invariant(org, 'Invalid org id');

      if (user) {
        if (!org.admins.find((a) => a.id === user.id)) {
          dispatch(updateOrg({ ...org, admins: [...org.admins, user] }));
        } else {
          dispatch(
            updateOrg({
              ...org,
              admins: [user, ...org.admins.filter((a) => a.id !== user.id)],
            })
          );
        }
      } else if (invite) {
        if (!org.invites.find((i) => i.id === invite.id)) {
          dispatch(updateOrg({ ...org, invites: [...org.invites, invite] }));
        }
      }

      return {
        user,
        invite,
      };
    });
  };
}

export function makeOwner(orgId: string, userId: string) {
  return (
    dispatch: Dispatch<MainStoreOrgDashboardOrgsAction>,
    getState: () => { orgDashboard: { orgs: MainStoreOrgDashboardOrgsState } }
  ) => {
    const org = getState().orgDashboard.orgs.byId[orgId];
    const admin = org.admins.find((a) => a.id === userId);
    invariant(admin, 'User is not an admin');
    return apiAddOwner(orgId, admin.email).then(({ user }) => {
      invariant(user, 'Missing user');
      dispatch(
        updateOrg({
          ...org,
          admins: [user, ...org.admins.filter((a) => a.id !== user.id)],
        })
      );
    });
  };
}

export function removeAdmin(orgId: string, userId: string) {
  return (
    dispatch: Dispatch<
      MainStoreOrgDashboardOrgsAction | MainStoreUserEnvironmentAction
    >,
    getState: () => {
      orgDashboard: { orgs: MainStoreOrgDashboardOrgsState };
      userEnvironment: MainStoreUserEnvironmentState;
    }
  ) => {
    const state = getState();
    const userEnvironment = cloneDeep(state.userEnvironment);
    userEnvironment.user.orgs = userEnvironment.user.orgs.filter(
      (o) => o.id !== orgId
    );
    const org = state.orgDashboard.orgs.byId[orgId];
    const admin = org.admins.find((a) => a.id === userId);
    invariant(admin, 'User is not an admin');
    return apiRemoveAdmin(orgId, admin.id).then(() => {
      dispatch(
        updateOrg({
          ...org,
          admins: org.admins.filter((a) => a.id !== admin.id),
        })
      );

      if (userEnvironment.user.id === admin.id) {
        dispatch(updateUserEnvironment(userEnvironment));
      }
    });
  };
}

export function approveAdminRequest(orgId: string, requestId: string) {
  return (
    dispatch: Dispatch<MainStoreOrgDashboardOrgsAction>,
    getState: () => { orgDashboard: { orgs: MainStoreOrgDashboardOrgsState } }
  ) => {
    const org = getState().orgDashboard.orgs.byId[orgId];
    const adminRequest = org.adminRequests.find((r) => r.id === requestId);
    invariant(adminRequest, 'No admin request found');
    return apiApproveAdminRequest(orgId, requestId).then((admin) => {
      dispatch(
        updateOrg({
          ...org,
          adminRequests: org.adminRequests.filter((r) => r.id !== requestId),
          admins: [admin, ...org.admins],
        })
      );
    });
  };
}

export function rejectAdminRequest(orgId: string, requestId: string) {
  return (
    dispatch: Dispatch<MainStoreOrgDashboardOrgsAction>,
    getState: () => { orgDashboard: { orgs: MainStoreOrgDashboardOrgsState } }
  ) => {
    const org = getState().orgDashboard.orgs.byId[orgId];
    const adminRequest = org.adminRequests.find((r) => r.id === requestId);
    invariant(adminRequest, 'No admin request found');
    return apiRejectAdminRequest(orgId, requestId).then(() => {
      dispatch(
        updateOrg({
          ...org,
          adminRequests: org.adminRequests.filter((r) => r.id !== requestId),
        })
      );
    });
  };
}

export function partialUpdateOrg(
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  values: Record<string, any>
): PartialUpdateOrgAction {
  return {
    type: 'ORG_DASHBOARD_PARTIAL_UPDATE_ORG',
    values,
  };
}
